// CLARTEXT · /abo — Plakat-Viewer (Modal).
//
// Zeigt ein reales, im Produkt erzeugtes Plakat an:
//  - type "pdf"   → das VOLLSTÄNDIGE echte PDF, ALLE Seiten, vertikal scrollbar.
//                   Gerendert client-seitig mit pdf.js (echte PDF-Bytes, kein
//                   Screenshot, kein Crop). Ersetzt das alte <iframe>, das große
//                   PDFs in vielen Browsern nur als Download/leer anzeigte.
//  - type "image" → Bild-Lightbox (z. B. Haut-/Handschutz-Poster).
// Plus „In neuem Tab öffnen" und „Herunterladen" (immer das echte PDF).
// Schließen per ESC / Backdrop.
import { useEffect, useRef, useState } from "react";
import { X, ExternalLink, Download, Loader2 } from "lucide-react";
import * as pdfjsLib from "pdfjs-dist/build/pdf";

// Worker same-origin (CSP: worker-src 'self' blob:). Version an pdfjs-dist
// gekoppelt (package.json exakt gepinnt); Datei liegt unter public/.
pdfjsLib.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.js";

// Logische Renderbreite (CSS max); pro Seite mit DPR hochgerendert für Schärfe,
// Anzeige über width:100% → passt sich Desktop wie 390px an (kein Overflow).
const RENDER_WIDTH = 900;

function PdfPages({ src }) {
  const wrapRef = useRef(null);
  const [status, setStatus] = useState("loading"); // loading | ready | error
  const [pageCount, setPageCount] = useState(0);

  useEffect(() => {
    let cancelled = false;
    let task = null;
    const wrap = wrapRef.current;

    async function run() {
      setStatus("loading");
      setPageCount(0);
      if (wrap) wrap.innerHTML = "";
      try {
        task = pdfjsLib.getDocument(src);
        const pdf = await task.promise;
        if (cancelled) return;
        setPageCount(pdf.numPages);
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        for (let n = 1; n <= pdf.numPages; n += 1) {
          const pageObj = await pdf.getPage(n); // eslint-disable-line no-await-in-loop
          if (cancelled) return;
          const base = pageObj.getViewport({ scale: 1 });
          const scale = (RENDER_WIDTH * dpr) / base.width;
          const viewport = pageObj.getViewport({ scale });
          const canvas = document.createElement("canvas");
          canvas.className = "abo-viewer-page";
          canvas.width = Math.floor(viewport.width);
          canvas.height = Math.floor(viewport.height);
          canvas.style.width = "100%";
          canvas.style.maxWidth = `${RENDER_WIDTH}px`;
          canvas.style.height = "auto";
          canvas.setAttribute("data-page", String(n));
          if (wrap) wrap.appendChild(canvas);
          const ctx = canvas.getContext("2d");
          // eslint-disable-next-line no-await-in-loop
          await pageObj.render({ canvasContext: ctx, viewport }).promise;
          if (cancelled) return;
        }
        if (!cancelled) setStatus("ready");
      } catch (err) {
        if (!cancelled) setStatus("error");
      }
    }

    run();
    return () => {
      cancelled = true;
      if (task && typeof task.destroy === "function") task.destroy();
    };
  }, [src]);

  return (
    <div
      className="abo-viewer-pages"
      data-testid="abo-viewer-pdf-pages"
      data-status={status}
      data-pages={pageCount}
    >
      {status === "loading" && (
        <div className="abo-viewer-note" data-testid="abo-viewer-pdf-loading">
          <Loader2 size={18} className="spin" aria-hidden="true" />
          Plakat wird geladen …
        </div>
      )}
      {status === "error" && (
        <div className="abo-viewer-note" data-testid="abo-viewer-pdf-error">
          Plakat konnte nicht geladen werden.{" "}
          <a href={src} target="_blank" rel="noreferrer">
            In neuem Tab öffnen
          </a>
          .
        </div>
      )}
      <div className="abo-viewer-canvaswrap" ref={wrapRef} aria-hidden={status !== "ready"} />
    </div>
  );
}

export default function PlakatViewer({ open, onClose, type, src, title, testId }) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="abo-viewer-backdrop"
      data-testid={testId}
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onClick={onClose}
    >
      <div className="abo-viewer" onClick={(e) => e.stopPropagation()}>
        <div className="abo-viewer-head">
          <span className="abo-viewer-title">{title}</span>
          <div className="abo-viewer-actions">
            <a
              className="abo-viewer-btn"
              href={src}
              target="_blank"
              rel="noreferrer"
              data-testid={`${testId}-newtab`}
            >
              <ExternalLink size={15} aria-hidden="true" />
              <span className="abo-viewer-btn-label">In neuem Tab</span>
            </a>
            <a
              className="abo-viewer-btn"
              href={src}
              download
              data-testid={`${testId}-download`}
            >
              <Download size={15} aria-hidden="true" />
              <span className="abo-viewer-btn-label">Herunterladen</span>
            </a>
            <button
              type="button"
              className="abo-viewer-close"
              onClick={onClose}
              data-testid={`${testId}-close`}
              aria-label="Plakat schließen"
            >
              <X size={18} aria-hidden="true" />
            </button>
          </div>
        </div>
        <div className="abo-viewer-body">
          {type === "pdf" ? (
            <PdfPages src={src} />
          ) : (
            <img src={src} alt={title} className="abo-viewer-img" />
          )}
        </div>
      </div>
    </div>
  );
}
